import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import { CheckCircle, Key, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedKey(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const pollStatus = useCallback(async () => {
    if (!sessionId || !token) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === "paid" || response.data.payment_status === "paid") {
        setStatus("success");
        if (response.data.order_id) {
          const orderRes = await axios.get(`${API}/orders/${response.data.order_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setOrder(orderRes.data);
        }
      } else if (response.data.status === "expired") {
        setStatus("expired");
      } else if (pollAttempts < 5) {
        setPollAttempts(prev => prev + 1);
      } else {
        setStatus("pending");
      }
    } catch {
      if (pollAttempts < 5) {
        setPollAttempts(prev => prev + 1);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, token, pollAttempts]);

  useEffect(() => {
    if (status === "checking" && pollAttempts <= 5) {
      const timer = setTimeout(pollStatus, pollAttempts === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [pollStatus, status, pollAttempts]);

  return (
    <div className="min-h-screen bg-[#050508]" data-testid="checkout-success-page">
      <Navbar />

      <main className="pt-24 pb-20 flex items-center justify-center min-h-screen">
        <div className="max-w-lg w-full mx-auto px-4">
          {status === "checking" && (
            <div className="text-center bg-[#0a0a12] border border-white/5 p-12">
              <Loader2 className="w-16 h-16 text-[#4F7CFF] mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl text-white mb-4">Processing Payment</h1>
              <p className="text-[#A1A1AA]">Please wait while we confirm your payment...</p>
              <p className="text-[#A1A1AA] text-sm mt-4">Attempt {pollAttempts + 1} of 5</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center bg-[#0a0a12] border border-[#4F7CFF]/30 p-12">
              <div className="w-20 h-20 bg-[#4F7CFF]/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-[#4F7CFF]" />
              </div>
              <h1 className="font-heading text-2xl text-white mb-4">Payment Successful!</h1>
              <p className="text-[#A1A1AA] mb-6">
                Thank you for your purchase! To receive your product, please join our Discord and open a support ticket.
              </p>

              {order && (
                <div className="bg-black/50 border border-white/5 p-6 mb-6 text-left">
                  <div className="flex items-center gap-2 mb-4">
                    <Key className="w-5 h-5 text-[#4F7CFF]" />
                    <span className="text-[#A1A1AA] uppercase text-sm tracking-wider">Order Details</span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Product</span>
                      <span className="text-white">{order.product_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Duration</span>
                      <span className="text-white uppercase">{order.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Order ID</span>
                      <div className="flex items-center gap-2">
                        <code className="text-[#4F7CFF] font-mono text-sm">{order.id}</code>
                        <button
                          onClick={() => copyToClipboard(order.id)}
                          className="text-[#A1A1AA] hover:text-[#4F7CFF] transition-colors"
                          data-testid="copy-order-id"
                        >
                          {copiedKey ? (
                            <Check className="w-4 h-4 text-[#4F7CFF]" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-[#5865F2]/10 border border-[#5865F2]/30 p-6 mb-6 text-left">
                <h3 className="font-heading text-lg text-white mb-3 flex items-center gap-2">
                  <DiscordIcon className="w-5 h-5 text-[#5865F2]" />
                  Next Steps
                </h3>
                <ol className="text-[#A1A1AA] space-y-2 list-decimal list-inside">
                  <li>Join our Discord server</li>
                  <li>Open a support ticket in the #tickets channel</li>
                  <li>Provide your <span className="text-white">Order ID</span> or a screenshot of this page</li>
                  <li>Our team will deliver your product within 24 hours</li>
                </ol>
              </div>

              <a
                href="https://discord.gg/efeySGKGJk"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full flex items-center justify-center gap-2 mb-4"
                data-testid="join-discord-btn"
              >
                <DiscordIcon className="w-5 h-5" />
                Join Discord & Open Ticket
              </a>

              <Link to="/dashboard" className="btn-secondary w-full block text-center" data-testid="go-to-dashboard">
                View My Orders
              </Link>
            </div>
          )}

          {status === "pending" && (
            <div className="text-center bg-[#0a0a12] border border-[#F5A623]/30 p-12">
              <Loader2 className="w-16 h-16 text-[#F5A623] mx-auto mb-6" />
              <h1 className="font-heading text-2xl text-white mb-4">Payment Pending</h1>
              <p className="text-[#A1A1AA] mb-8">
                Your payment is still being processed. Please check your dashboard in a few minutes.
              </p>
              <Link to="/dashboard" className="btn-primary">
                Go to Dashboard
              </Link>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center bg-[#0a0a12] border border-[#FF3B30]/30 p-12">
              <div className="w-20 h-20 bg-[#FF3B30]/20 flex items-center justify-center mx-auto mb-6">
                <Key className="w-12 h-12 text-[#FF3B30]" />
              </div>
              <h1 className="font-heading text-2xl text-white mb-4">Session Expired</h1>
              <p className="text-[#A1A1AA] mb-8">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/products" className="btn-primary">
                Browse Products
              </Link>
            </div>
          )}

          {status === "error" && (
            <div className="text-center bg-[#0a0a12] border border-[#FF3B30]/30 p-12">
              <div className="w-20 h-20 bg-[#FF3B30]/20 flex items-center justify-center mx-auto mb-6">
                <Key className="w-12 h-12 text-[#FF3B30]" />
              </div>
              <h1 className="font-heading text-2xl text-white mb-4">Something Went Wrong</h1>
              <p className="text-[#A1A1AA] mb-8">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              <div className="flex gap-4">
                <Link to="/dashboard" className="btn-secondary flex-1">
                  Go to Dashboard
                </Link>
                <Link to="/products" className="btn-primary flex-1">
                  Try Again
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CheckoutSuccessPage;
